import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { authService } from '@/services/authService'

interface User {
  id: number
  username: string
  email: string
  nickname?: string
  avatarUrl?: string
  bio?: string
  status: string
  roles?: string[]
  emailVerified?: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

interface AuthErrorPayload {
  status?: number
  message: string
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { usernameOrEmail: string; password: string }) => {
    const response = await authService.login(credentials)
    return response
  }
)

export const register = createAsyncThunk(
  'auth/register',
  async (userData: { username: string; email: string; code: string; password: string }) => {
    const response = await authService.register(userData)
    return response
  }
)

export const getProfile = createAsyncThunk<User, void, { rejectValue: AuthErrorPayload }>(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getProfile()
      return response
    } catch (error: any) {
      return rejectWithValue({
        status: error?.status ?? error?.response?.status,
        message: error?.message || '获取用户信息失败',
      })
    }
  }
)

export const logout = createAsyncThunk('auth/logout', async () => {
  await authService.logout()
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
      localStorage.setItem('token', action.payload)
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
      localStorage.setItem('token', action.payload.token)
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.error = null
      localStorage.removeItem('token')
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        const response = action.payload
        state.user = response.user
        state.token = response.token
        state.isAuthenticated = true
        localStorage.setItem('token', response.token)
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '登录失败'
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false
        const response = action.payload
        state.user = response.user
        state.token = response.token
        state.isAuthenticated = true
        localStorage.setItem('token', response.token)
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '注册失败'
      })
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.loading = true
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || action.error.message || '获取用户信息失败'

        if (action.payload?.status === 401) {
          state.isAuthenticated = false
          state.token = null
          state.user = null
          localStorage.removeItem('token')
        }
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        localStorage.removeItem('token')
      })
  },
})

export const { clearError, setToken, setCredentials, clearAuth } = authSlice.actions
export default authSlice.reducer 
